import { useMemo, useState } from "react";
import { ProductRow } from "./types";
import { formatMoney } from "./helpers";

type SortKey = "category" | "unitsSold" | "grossSales" | "tax" | "discounts" | "netSales";

interface CategoryRow {
  category: string;
  unitsSold: number;
  grossSales: number;
  tax: number;
  discounts: number;
  netSales: number;
}

export function ProductMix({ rows }: { rows: ProductRow[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("category");
  const [sortAsc, setSortAsc] = useState(true);

  const categoryRows: CategoryRow[] = useMemo(() => {
    const map = new Map<string, CategoryRow>();
    for (const r of rows) {
      const existing = map.get(r.category);
      if (existing) {
        existing.unitsSold += r.unitsSold;
        existing.grossSales += r.grossSales;
        existing.tax += r.tax;
        existing.discounts += r.discounts;
        existing.netSales += r.netSales;
      } else {
        map.set(r.category, {
          category: r.category,
          unitsSold: r.unitsSold,
          grossSales: r.grossSales,
          tax: r.tax,
          discounts: r.discounts,
          netSales: r.netSales,
        });
      }
    }
    return Array.from(map.values());
  }, [rows]);

  const totals = useMemo(() => categoryRows.reduce(
    (acc, r) => {
      acc.units += r.unitsSold;
      acc.gross += r.grossSales;
      acc.tax += r.tax;
      acc.discounts += r.discounts;
      acc.net += r.netSales;
      return acc;
    },
    { units: 0, gross: 0, tax: 0, discounts: 0, net: 0 }
  ), [categoryRows]);

  const sorted = useMemo(() => {
    const copy = [...categoryRows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv)
        : (Number(av) - Number(bv));
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [categoryRows, sortKey, sortAsc]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === "category");
    }
  };

  const indicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? "↑" : "↓") : <span className="text-gray-300">↕</span>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
      <h2 className="text-sm font-semibold text-gray-900">Product mix summary</h2>
      <p className="text-xs text-gray-500 mt-0.5">Click a column to sort.</p>

      {categoryRows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No products sold.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th label="Category"  align="left"  active={sortKey === "category"}   onClick={() => sortBy("category")}   indicator={indicator("category")} />
                <Th label="Units"     align="right" active={sortKey === "unitsSold"}  onClick={() => sortBy("unitsSold")}  indicator={indicator("unitsSold")} />
                <Th label="Gross"     align="right" active={sortKey === "grossSales"} onClick={() => sortBy("grossSales")} indicator={indicator("grossSales")} />
                <Th label="Tax"       align="right" active={sortKey === "tax"}        onClick={() => sortBy("tax")}        indicator={indicator("tax")} />
                <Th label="Discounts" align="right" active={sortKey === "discounts"}  onClick={() => sortBy("discounts")}  indicator={indicator("discounts")} />
                <Th label="Net"       align="right" active={sortKey === "netSales"}   onClick={() => sortBy("netSales")}   indicator={indicator("netSales")} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((r) => (
                <tr key={r.category} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900">{r.category}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-700">{r.unitsSold.toLocaleString()}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">{formatMoney(r.grossSales)}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-500">{formatMoney(r.tax)}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-500">{formatMoney(r.discounts)}</td>
                  <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatMoney(r.netSales)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-3 py-2 text-sm text-gray-900">Total</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">{totals.units.toLocaleString()}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">{formatMoney(totals.gross)}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-700">{formatMoney(totals.tax)}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-700">{formatMoney(totals.discounts)}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">{formatMoney(totals.net)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({
  label,
  align,
  active,
  onClick,
  indicator,
}: {
  label: string;
  align: "left" | "right";
  active: boolean;
  onClick: () => void;
  indicator: React.ReactNode;
}) {
  return (
    <th
      onClick={onClick}
      className={`px-3 py-2 text-${align} text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${
        active ? "text-gray-700" : "text-gray-500"
      } hover:text-gray-700`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        <span className="ml-0.5">{indicator}</span>
      </span>
    </th>
  );
}
